import { Injectable } from '@angular/core';
import { environment } from '../../../environment';

/**
 * Runtime application configuration.
 *
 * The compiled Angular bundle is environment-agnostic: instead of baking the
 * backend URL into the build, the container entrypoint writes it to
 * `/assets/config.json`. The immutable release version is baked into
 * `/assets/version.json` while building the image. This service loads both
 * files before the app bootstraps (via APP_INITIALIZER).
 *
 * If the file is missing or unreadable (e.g. local `ng serve`), the default
 * `environment.apiUrl` is kept.
 */
interface RuntimeConfig {
  apiUrl?: string;
}

interface VersionConfig {
  version?: string;
}

@Injectable({ providedIn: 'root' })
export class AppConfigService {
  private applicationVersion = 'dev';

  get version(): string {
    return this.applicationVersion;
  }

  /**
   * Loads runtime and build-time configuration. Always resolves so a missing
   * file falls back to defaults instead of blocking application startup.
   */
  async load(): Promise<void> {
    await Promise.all([this.loadRuntimeConfig(), this.loadVersionConfig()]);
  }

  private async loadRuntimeConfig(): Promise<void> {
    try {
      const response = await fetch(`assets/config.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        console.warn(`[AppConfigService] config.json not available (HTTP ${response.status}); using defaults.`);
        return;
      }

      const config = (await response.json()) as RuntimeConfig;

      // Only override when a non-empty, non-placeholder value is present.
      // The `${` guard covers the edge case where the container entrypoint did
      // not substitute the template (e.g. a raw `${API_URL}` slipped through),
      // so we keep the compiled default instead of a broken URL.
      if (config.apiUrl && config.apiUrl.trim() && !config.apiUrl.includes('${')) {
        environment.apiUrl = config.apiUrl.trim();
      }
    } catch (error) {
      console.warn('[AppConfigService] Failed to load config.json; using default apiUrl.', error);
    }
  }

  private async loadVersionConfig(): Promise<void> {
    try {
      const response = await fetch(`assets/version.json?t=${Date.now()}`, {
        cache: 'no-store',
      });

      if (!response.ok) {
        console.warn(`[AppConfigService] version.json not available (HTTP ${response.status}); using dev.`);
        return;
      }

      const config = (await response.json()) as VersionConfig;
      if (config.version && config.version.trim() && !config.version.includes('${')) {
        this.applicationVersion = config.version.trim();
      }
    } catch (error) {
      console.warn('[AppConfigService] Failed to load version.json; using dev.', error);
    }
  }
}
